import { useState, useEffect, useRef } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import axios from "axios";
import { motion, AnimatePresence } from "framer-motion";
import {
  User,
  Users,
  Mail,
  MapPin,
  Globe,
  Instagram,
  Twitter,
  ArrowLeft,
  Calendar,
  Image,
  Check,
  FileStack,
  LogOut,
  Camera,
  Upload,
  Trash2,
  X,
  Plus,
  UserCheck,
  Pencil,
  Save,
  Coins,
  Gift
} from "lucide-react";
import { compressImage, compressionPresets } from "../utils/imageCompression";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
axios.defaults.withCredentials = true;
const API = `${BACKEND_URL}/api`;

// Helper to get correct image data URI
const getImageSrc = (base64Data) => {
  if (!base64Data) return null;
  if (base64Data.startsWith("data:")) return base64Data;
  
  if (base64Data.startsWith("UklGR")) {
    return `data:image/webp;base64,${base64Data}`;
  } else if (base64Data.startsWith("/9j/")) {
    return `data:image/jpeg;base64,${base64Data}`;
  } else if (base64Data.startsWith("iVBOR")) {
    return `data:image/png;base64,${base64Data}`;
  }
  return `data:image/jpeg;base64,${base64Data}`;
};

const AdminFanProfilePage = () => {
  const { fanId } = useParams();
  const navigate = useNavigate();
  const [fan, setFan] = useState(null);
  const [loading, setLoading] = useState(true);
  const [currentUser, setCurrentUser] = useState(null);
  // Content management state
  const [showUploadModal, setShowUploadModal] = useState(false);
  const [uploadFile, setUploadFile] = useState(null);
  const [uploadPreview, setUploadPreview] = useState(null);
  const [uploadTitle, setUploadTitle] = useState("");
  const [uploadDescription, setUploadDescription] = useState("");
  const [uploadCategory, setUploadCategory] = useState("free");
  const [uploading, setUploading] = useState(false);
  const [deletingContentId, setDeletingContentId] = useState(null);
  const [impersonating, setImpersonating] = useState(false);
  const fileInputRef = useRef(null);
  // Edit content state
  const [showEditContentModal, setShowEditContentModal] = useState(false);
  const [editingContent, setEditingContent] = useState(null);
  const [editContentForm, setEditContentForm] = useState({ title: "", description: "", category: "free" });
  const [savingContent, setSavingContent] = useState(false);

  useEffect(() => {
    const storedUser = localStorage.getItem("user");
    if (!storedUser) {
      navigate("/login");
      return;
    }
    
    const userData = JSON.parse(storedUser);
    if (userData.role !== "admin") {
      navigate("/admin");
      return;
    }
    
    setCurrentUser(userData);
    fetchFan();
  }, [fanId, navigate]);

  const fetchFan = async () => {
    try {
      
      const response = await axios.get(`${API}/admin/fans/${fanId}`, {
        headers: {}
      });
      setFan(response.data);
    } catch (err) {
      if (err.response?.status === 404) {
        navigate("/admin/users");
      }
    } finally {
      setLoading(false);
    }
  };

  const handleLogout = async () => {
    try {
      await axios.post(`${API}/auth/logout`);
    } catch (err) {
      console.error("Logout failed:", err.message);
    }
    localStorage.removeItem("token");
    localStorage.removeItem("user");
    navigate("/login");
  };

  const impersonateUser = async () => {
    if (!window.confirm(`Impersonate ${fan.name}? You will be logged in as this user.`)) return;
    
    setImpersonating(true);
    try {
      
      const response = await axios.post(
        `${API}/admin/impersonate/${fanId}`,
        {},
        { headers: {} }
      );
      
      // Store original admin credentials in sessionStorage
      // Store impersonation state in sessionStorage (for UI purposes only)
      sessionStorage.setItem("impersonation", JSON.stringify({
        originalUser: currentUser,
        impersonatedUser: response.data.user,
        impersonatedBy: response.data.impersonated_by
      }));
      
      // Store user info (token is in httpOnly cookie set by server)
      localStorage.setItem("user", JSON.stringify(response.data.user));
      
      // Redirect to profile
      navigate("/app/profile");
      window.location.reload();
    } catch (err) {
      alert(err.response?.data?.detail || "Failed to impersonate user");
    } finally {
      setImpersonating(false);
    }
  };

  // Content Management Functions
  const handleFileSelect = (e) => {
    const file = e.target.files[0];
    if (file) {
      const isImage = file.type.startsWith("image/");
      const isVideo = file.type.startsWith("video/");
      
      if (!isImage && !isVideo) {
        alert("Please select an image or video file");
        return;
      }
      
      // Different size limits: 100MB for videos, 10MB for images
      const maxSize = isVideo ? 100 * 1024 * 1024 : 10 * 1024 * 1024;
      if (file.size > maxSize) {
        alert(`File too large. Maximum size is ${isVideo ? '100MB' : '10MB'}`);
        return;
      }
      
      setUploadFile(file);
      const reader = new FileReader();
      reader.onloadend = () => setUploadPreview(reader.result);
      reader.readAsDataURL(file);
    }
  };

  const openUploadModal = () => {
    setShowUploadModal(true);
    setUploadFile(null);
    setUploadPreview(null);
    setUploadTitle("");
    setUploadDescription("");
    setUploadCategory("free");
  };

  const closeUploadModal = () => {
    setShowUploadModal(false);
    setUploadFile(null);
    setUploadPreview(null);
    setUploadTitle("");
    setUploadDescription("");
    setUploadCategory("free");
  };

  const handleUploadContent = async () => {
    if (!uploadFile) {
      alert("Please select a file to upload");
      return;
    }
    
    setUploading(true);
    try {
      // Only compress images, not videos
      const isVideo = uploadFile.type.startsWith("video/");
      const fileToUpload = isVideo ? uploadFile : await compressImage(uploadFile, compressionPresets.content);
      
      const formData = new FormData();
      formData.append("file", fileToUpload);
      if (uploadTitle) formData.append("title", uploadTitle);
      if (uploadDescription) formData.append("description", uploadDescription);
      formData.append("category", uploadCategory);
      
      await axios.post(
        `${API}/admin/users/${fanId}/content`,
        formData,
        { 
          headers: { 
            
            "Content-Type": "multipart/form-data"
          } 
        }
      );
      
      // Refresh fan data to get updated content
      await fetchFan();
      closeUploadModal();
    } catch (err) {
      alert(err.response?.data?.detail || "Failed to upload content");
    } finally {
      setUploading(false);
    }
  };

  const handleDeleteContent = async (contentId) => {
    if (!window.confirm("Are you sure you want to delete this content?")) return;
    
    setDeletingContentId(contentId);
    try {
      
      await axios.delete(
        `${API}/admin/users/${fanId}/content/${contentId}`,
        { headers: {} }
      );
      
      // Update local state
      setFan(prev => ({
        ...prev,
        content: prev.content.filter(c => c.id !== contentId)
      }));
    } catch (err) {
      alert(err.response?.data?.detail || "Failed to delete content");
    } finally {
      setDeletingContentId(null);
    }
  };

  // Edit Content Functions
  const openEditContentModal = (content) => {
    setEditingContent(content);
    setEditContentForm({
      title: content.title || "",
      description: content.description || "",
      category: content.category || "free"
    });
    setShowEditContentModal(true);
  };

  const closeEditContentModal = () => {
    setShowEditContentModal(false);
    setEditingContent(null);
    setEditContentForm({ title: "", description: "", category: "free" });
  };

  const handleSaveContent = async () => {
    if (!editingContent) return;
    
    setSavingContent(true);
    try {
      
      await axios.patch(
        `${API}/admin/users/${fanId}/content/${editingContent.id}`,
        editContentForm,
        { headers: {} }
      );
      
      // Update local state
      setFan(prev => ({
        ...prev,
        content: prev.content.map(c => 
          c.id === editingContent.id 
            ? { ...c, ...editContentForm }
            : c
        )
      }));
      closeEditContentModal();
    } catch (err) {
      alert(err.response?.data?.detail || "Failed to update content");
    } finally {
      setSavingContent(false);
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return "N/A";
    return new Date(dateString).toLocaleDateString("en-US", {
      year: "numeric",
      month: "short",
      day: "numeric"
    });
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-900 flex items-center justify-center">
        <div className="w-12 h-12 border-4 border-[#00A8E8] border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  if (!fan) {
    return (
      <div className="min-h-screen bg-gray-900 flex items-center justify-center">
        <p className="text-gray-500">Fan not found</p>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-900" data-testid="admin-fan-profile">
      {/* Header */}
      <header className="bg-gray-800/50 border-b border-gray-700/50 sticky top-0 z-50 backdrop-blur-xl">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-6">
              <h1 className="text-2xl font-bold" style={{ fontFamily: "'Montserrat', sans-serif", letterSpacing: '-0.02em' }}>
                <span className="text-white">HotPeek</span>
              </h1>
              <nav className="flex items-center gap-1">
                <Link
                  to="/admin"
                  className="flex items-center gap-2 px-3 py-2 text-gray-400 hover:text-white hover:bg-gray-700/50 rounded-lg transition-colors"
                  data-testid="nav-applications"
                >
                  <FileStack size={18} />
                  <span className="hidden sm:inline">Applications</span>
                </Link>
                <Link
                  to="/admin/users"
                  className="flex items-center gap-2 px-3 py-2 text-white bg-gray-700/50 rounded-lg transition-colors"
                  data-testid="nav-users"
                >
                  <Users size={18} />
                  <span className="hidden sm:inline">Users</span>
                </Link>
              </nav>
            </div>
            <div className="flex items-center gap-4">
              <div className="text-right hidden sm:block">
                <p className="text-white font-medium">{currentUser?.name}</p>
                <p className="text-gray-500 text-sm capitalize">{currentUser?.role}</p>
              </div>
              <button
                onClick={handleLogout}
                className="p-2 text-gray-400 hover:text-white hover:bg-gray-700 rounded-lg transition-colors"
                title="Logout"
              >
                <LogOut size={20} />
              </button>
            </div>
          </div>
        </div>
      </header>

      <main className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Back button */}
        <button
          onClick={() => navigate("/admin/users")}
          className="flex items-center gap-2 text-gray-400 hover:text-white mb-6 transition-colors"
        >
          <ArrowLeft size={20} />
          Back to Users
        </button>

        {/* Profile Header */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="bg-gray-800/50 rounded-2xl border border-gray-700/50 overflow-hidden mb-6"
        >
          {/* Header Image */}
          <div className="h-32 bg-gradient-to-r from-green-600/30 to-emerald-600/30 relative">
            {fan.header_image && (
              <img
                src={getImageSrc(fan.header_image)}
                alt="Header"
                className="w-full h-full object-cover"
              />
            )}
          </div>

          {/* Profile Info */}
          <div className="px-6 pb-6">
            {/* Avatar */}
            <div className="relative -mt-16 mb-4">
              <div className="w-28 h-28 rounded-2xl border-4 border-gray-800 overflow-hidden bg-gray-700">
                {fan.profile_image ? (
                  <img
                    src={getImageSrc(fan.profile_image)}
                    alt={fan.name}
                    className="w-full h-full object-cover"
                  />
                ) : (
                  <div className="w-full h-full flex items-center justify-center bg-green-500/20">
                    <User size={48} className="text-green-400" />
                  </div>
                )}
              </div>
            </div>

            <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-4">
              <div>
                <div className="flex items-center gap-3 mb-2">
                  <h2 className="text-2xl font-bold text-white">{fan.name}</h2>
                  <span className="px-3 py-1 rounded-full text-xs font-medium bg-green-500/20 text-green-400 border border-green-500/30">
                    Fan
                  </span>
                  {fan.is_verified && (
                    <span className="px-3 py-1 rounded-full text-xs font-medium bg-blue-500/20 text-blue-400 border border-blue-500/30 flex items-center gap-1">
                      <Check size={12} />
                      Verified
                    </span>
                  )}
                  {!fan.is_active && (
                    <span className="px-3 py-1 rounded-full text-xs font-medium bg-red-500/20 text-red-400 border border-red-500/30">
                      Deactivated
                    </span>
                  )}
                </div>
                
                <div className="flex flex-wrap items-center gap-4 text-gray-400 text-sm">
                  <span className="flex items-center gap-1">
                    <Mail size={14} />
                    {fan.email}
                  </span>
                  {fan.location && (
                    <span className="flex items-center gap-1">
                      <MapPin size={14} />
                      {fan.location}
                    </span>
                  )}
                  <span className="flex items-center gap-1">
                    <Calendar size={14} />
                    Joined {formatDate(fan.created_at)}
                  </span>
                  <span className="flex items-center gap-1 text-amber-400 font-medium">
                    <Coins size={14} />
                    {fan.credits || 0} credits
                  </span>
                  {/* Invited count */}
                  {fan.invited_count > 0 && (
                    <span className="flex items-center gap-1 text-green-400">
                      <Users size={14} />
                      {fan.invited_count} invited
                    </span>
                  )}
                </div>

                {/* Inviter Info */}
                {fan.inviter && (
                  <div className="mt-3 flex items-center gap-2 p-2 bg-green-500/10 border border-green-500/20 rounded-lg">
                    <Gift size={14} className="text-green-400" />
                    <span className="text-sm text-green-300">
                      Invited by: <span className="font-medium">{fan.inviter.name}</span>
                      <span className="text-green-400/70 ml-1">({fan.inviter.email})</span>
                    </span>
                  </div>
                )}

                {fan.bio && (
                  <p className="text-gray-300 mt-3">{fan.bio}</p>
                )}
              </div>

              {/* Action Buttons */}
              <div className="flex flex-wrap gap-2">
                <button
                  onClick={impersonateUser}
                  disabled={impersonating}
                  className="flex items-center gap-2 px-4 py-2 bg-indigo-500/20 text-indigo-400 border border-indigo-500/30 hover:bg-indigo-500/30 rounded-lg transition-colors text-sm font-medium disabled:opacity-50"
                  data-testid="impersonate-btn"
                >
                  {impersonating ? (
                    <div className="w-4 h-4 border-2 border-current/30 border-t-current rounded-full animate-spin" />
                  ) : (
                    <UserCheck size={16} />
                  )}
                  Impersonate
                </button>
              </div>
            </div>
          </div>
        </motion.div>

        {/* Social Links */}
        {(fan.instagram || fan.twitter || fan.website) && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.1 }}
            className="bg-gray-800/50 rounded-2xl border border-gray-700/50 p-6 mb-6"
          >
            <h3 className="text-lg font-semibold text-white mb-4">Social Links</h3>
            <div className="flex flex-wrap gap-3">
              {fan.instagram && (
                <a
                  href={`https://instagram.com/${fan.instagram.replace("@", "")}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex items-center gap-2 px-4 py-2 bg-pink-500/10 text-pink-400 border border-pink-500/30 rounded-lg hover:bg-pink-500/20 transition-colors"
                >
                  <Instagram size={16} />
                  {fan.instagram}
                </a>
              )}
              {fan.twitter && (
                <a
                  href={`https://twitter.com/${fan.twitter.replace("@", "")}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex items-center gap-2 px-4 py-2 bg-blue-500/10 text-blue-400 border border-blue-500/30 rounded-lg hover:bg-blue-500/20 transition-colors"
                >
                  <Twitter size={16} />
                  {fan.twitter}
                </a>
              )}
              {fan.website && (
                <a
                  href={fan.website.startsWith("http") ? fan.website : `https://${fan.website}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex items-center gap-2 px-4 py-2 bg-gray-700/50 text-gray-300 border border-gray-600/30 rounded-lg hover:bg-gray-700 transition-colors"
                >
                  <Globe size={16} />
                  Website
                </a>
              )}
            </div>
          </motion.div>
        )}

        {/* Content Section */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.2 }}
          className="bg-gray-800/50 rounded-2xl border border-gray-700/50 p-6"
        >
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-lg font-semibold text-white flex items-center gap-2">
              <Image size={20} className="text-green-400" />
              Content ({fan.content?.length || 0} images)
            </h3>
            <button
              onClick={openUploadModal}
              className="flex items-center gap-2 px-4 py-2 bg-gradient-to-r from-[#00A8E8] to-[#D000FF] text-white font-medium rounded-lg hover:opacity-90 transition-opacity text-sm"
              data-testid="upload-content-btn"
            >
              <Upload size={16} />
              Upload Content
            </button>
          </div>

          {fan.content && fan.content.length > 0 ? (
            <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-4">
              {fan.content.map((item, index) => (
                <motion.div
                  key={item.id || index}
                  initial={{ opacity: 0, scale: 0.9 }}
                  animate={{ opacity: 1, scale: 1 }}
                  transition={{ delay: index * 0.05 }}
                  className="group relative aspect-square rounded-xl overflow-hidden bg-gray-700/50 border border-gray-600/30"
                  data-testid={`content-item-${item.id}`}
                >
                  {item.media_type === "video" || item.image_data?.startsWith("data:video/") ? (
                    <video
                      src={item.image_data}
                      className="w-full h-full object-cover"
                      muted
                      loop
                      playsInline
                      onMouseOver={(e) => e.target.play()}
                      onMouseOut={(e) => { e.target.pause(); e.target.currentTime = 0; }}
                    />
                  ) : (
                    <img
                      src={getImageSrc(item.image_data)}
                      alt={item.title || `Content ${index + 1}`}
                      className="w-full h-full object-cover"
                    />
                  )}
                  {/* Badges - Top Left (Video, Category) */}
                  <div className="absolute top-2 left-2 flex flex-col gap-1 z-10">
                    {/* Video indicator */}
                    {(item.media_type === "video" || item.image_data?.startsWith("data:video/")) && (
                      <span className="px-2 py-0.5 rounded-full text-xs font-medium bg-purple-500/90 text-white flex items-center gap-1">
                        <svg className="w-3 h-3" viewBox="0 0 24 24" fill="currentColor"><path d="M8 5v14l11-7z"/></svg>
                        Video
                      </span>
                    )}
                    {/* Category badge */}
                    <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${
                      item.category === "premium" 
                        ? "bg-amber-500/90 text-black" 
                        : "bg-green-500/90 text-white"
                    }`}>
                      {item.category === "premium" ? "Premium" : "Free"}
                    </span>
                  </div>
                  {/* Title - Bottom */}
                  {item.title && (
                    <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/80 to-transparent p-2 pt-6">
                      <p className="text-white text-xs font-medium truncate">{item.title}</p>
                    </div>
                  )}
                  {/* Hover overlay with edit and delete buttons */}
                  <div className="absolute inset-0 bg-black/60 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center gap-2">
                    <button
                      onClick={() => openEditContentModal(item)}
                      className="p-3 bg-blue-500/80 hover:bg-blue-500 text-white rounded-full transition-colors"
                      title="Edit content"
                      data-testid={`edit-content-${item.id}`}
                    >
                      <Pencil size={20} />
                    </button>
                    <button
                      onClick={() => handleDeleteContent(item.id)}
                      disabled={deletingContentId === item.id}
                      className="p-3 bg-red-500/80 hover:bg-red-500 text-white rounded-full transition-colors disabled:opacity-50"
                      title="Delete content"
                      data-testid={`delete-content-${item.id}`}
                    >
                      {deletingContentId === item.id ? (
                        <div className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                      ) : (
                        <Trash2 size={20} />
                      )}
                    </button>
                  </div>
                  {/* Title overlay */}
                  {item.title && (
                    <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/80 to-transparent p-2">
                      <p className="text-white text-xs truncate">{item.title}</p>
                    </div>
                  )}
                </motion.div>
              ))}
            </div>
          ) : (
            <div className="text-center py-12">
              <Camera size={48} className="mx-auto text-gray-600 mb-4" />
              <p className="text-gray-500">No content uploaded yet</p>
              <p className="text-gray-600 text-sm mt-2">Click "Upload Content" to add images for this fan</p>
            </div>
          )}
        </motion.div>
      </main>

      {/* Upload Modal */}
      <AnimatePresence>
        {showUploadModal && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/60 backdrop-blur-sm z-50 flex items-center justify-center p-4 overflow-y-auto"
            onClick={closeUploadModal}
          >
            <motion.div
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0, scale: 0.95 }}
              onClick={(e) => e.stopPropagation()}
              className="bg-gray-800 rounded-2xl w-full max-w-md border border-gray-700 shadow-2xl my-8 flex flex-col max-h-[90vh]"
            >
              {/* Modal Header */}
              <div className="flex items-center justify-between p-6 border-b border-gray-700 flex-shrink-0">
                <h3 className="text-xl font-semibold text-white">Upload Content</h3>
                <button
                  onClick={closeUploadModal}
                  className="p-2 text-gray-400 hover:text-white hover:bg-gray-700 rounded-lg transition-colors"
                >
                  <X size={20} />
                </button>
              </div>

              {/* Modal Content - Scrollable */}
              <div className="p-6 space-y-4 overflow-y-auto flex-1">
                {/* File Upload Area */}
                <div
                  onClick={() => fileInputRef.current?.click()}
                  className={`border-2 border-dashed rounded-xl p-6 text-center cursor-pointer transition-colors ${
                    uploadPreview 
                      ? "border-[#00A8E8]/50 bg-[#00A8E8]/5" 
                      : "border-gray-600 hover:border-gray-500 hover:bg-gray-700/30"
                  }`}
                >
                  <input
                    ref={fileInputRef}
                    type="file"
                    accept="image/*,video/*"
                    onChange={handleFileSelect}
                    className="hidden"
                  />
                  {uploadPreview ? (
                    <div className="space-y-3">
                      {uploadFile?.type?.startsWith("video/") ? (
                        <video
                          src={uploadPreview}
                          className="max-h-48 mx-auto rounded-lg object-contain"
                          controls
                          muted
                        />
                      ) : (
                        <img
                          src={uploadPreview}
                          alt="Preview"
                          className="max-h-48 mx-auto rounded-lg object-contain"
                        />
                      )}
                      <p className="text-sm text-gray-400">Click to change {uploadFile?.type?.startsWith("video/") ? "video" : "image"}</p>
                    </div>
                  ) : (
                    <div className="space-y-3">
                      <div className="w-16 h-16 mx-auto rounded-full bg-gray-700 flex items-center justify-center">
                        <Plus size={32} className="text-gray-400" />
                      </div>
                      <div>
                        <p className="text-white font-medium">Click to select an image or video</p>
                        <p className="text-sm text-gray-500">Images: JPEG, PNG, WebP, GIF up to 10MB</p>
                        <p className="text-sm text-gray-500">Videos: MP4, WebM, MOV up to 100MB</p>
                      </div>
                    </div>
                  )}
                </div>

                {/* Title Input */}
                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-2">Title (optional)</label>
                  <input
                    type="text"
                    value={uploadTitle}
                    onChange={(e) => setUploadTitle(e.target.value)}
                    placeholder="Enter a title for this content"
                    className="w-full px-4 py-3 bg-gray-700/50 border border-gray-600 rounded-lg text-white placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-[#00A8E8]/50 focus:border-[#00A8E8] transition-all"
                  />
                </div>

                {/* Description Input */}
                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-2">Description (optional)</label>
                  <textarea
                    value={uploadDescription}
                    onChange={(e) => setUploadDescription(e.target.value)}
                    placeholder="Enter a description"
                    rows={2}
                    className="w-full px-4 py-3 bg-gray-700/50 border border-gray-600 rounded-lg text-white placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-[#00A8E8]/50 focus:border-[#00A8E8] transition-all resize-none"
                  />
                </div>

                {/* Category Selection */}
                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-2">Category</label>
                  <div className="flex gap-3">
                    <button
                      type="button"
                      onClick={() => setUploadCategory("free")}
                      className={`flex-1 py-3 rounded-lg font-medium transition-all ${
                        uploadCategory === "free"
                          ? "bg-green-500/20 text-green-400 border-2 border-green-500"
                          : "bg-gray-700/50 text-gray-400 border border-gray-600 hover:border-gray-500"
                      }`}
                    >
                      Free
                    </button>
                    <button
                      type="button"
                      onClick={() => setUploadCategory("premium")}
                      className={`flex-1 py-3 rounded-lg font-medium transition-all ${
                        uploadCategory === "premium"
                          ? "bg-amber-500/20 text-amber-400 border-2 border-amber-500"
                          : "bg-gray-700/50 text-gray-400 border border-gray-600 hover:border-gray-500"
                      }`}
                    >
                      Premium
                    </button>
                  </div>
                  <p className="text-xs text-gray-500 mt-2">
                    Free content is visible to registered fans. Premium requires payment.
                  </p>
                </div>

                {/* Upload for user info */}
                <div className="bg-gray-700/30 rounded-lg p-3">
                  <p className="text-sm text-gray-400">
                    Uploading content for: <span className="text-white font-medium">{fan.name}</span>
                  </p>
                </div>

                {/* Action Buttons */}
                <div className="flex gap-3 pt-2">
                  <button
                    onClick={closeUploadModal}
                    className="flex-1 py-3 border border-gray-600 text-gray-300 font-medium rounded-lg hover:bg-gray-700 transition-colors"
                  >
                    Cancel
                  </button>
                  <button
                    onClick={handleUploadContent}
                    disabled={!uploadFile || uploading}
                    className="flex-1 py-3 bg-gradient-to-r from-[#00A8E8] to-[#D000FF] text-white font-medium rounded-lg hover:opacity-90 transition-opacity disabled:opacity-50 flex items-center justify-center gap-2"
                  >
                    {uploading ? (
                      <div className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                    ) : (
                      <>
                        <Upload size={18} />
                        Upload
                      </>
                    )}
                  </button>
                </div>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Edit Content Modal */}
      <AnimatePresence>
        {showEditContentModal && editingContent && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/60 backdrop-blur-sm z-50 flex items-center justify-center p-4"
            onClick={closeEditContentModal}
          >
            <motion.div
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0, scale: 0.95 }}
              onClick={(e) => e.stopPropagation()}
              className="bg-gray-800 rounded-2xl w-full max-w-md border border-gray-700 shadow-2xl"
            >
              {/* Modal Header */}
              <div className="flex items-center justify-between p-6 border-b border-gray-700">
                <h3 className="text-xl font-semibold text-white flex items-center gap-2">
                  <Pencil size={20} className="text-blue-400" />
                  Edit Content
                </h3>
                <button
                  onClick={closeEditContentModal}
                  className="p-2 text-gray-400 hover:text-white hover:bg-gray-700 rounded-lg transition-colors"
                >
                  <X size={20} />
                </button>
              </div>

              {/* Modal Content */}
              <div className="p-6 space-y-4">
                {/* Preview */}
                <div className="aspect-video rounded-lg overflow-hidden bg-gray-700/50">
                  <img
                    src={getImageSrc(editingContent.image_data)}
                    alt={editingContent.title || "Content"}
                    className="w-full h-full object-contain"
                  />
                </div>

                {/* Title Input */}
                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-2">Title</label>
                  <input
                    type="text"
                    value={editContentForm.title}
                    onChange={(e) => setEditContentForm(prev => ({ ...prev, title: e.target.value }))}
                    placeholder="Enter a title"
                    className="w-full px-4 py-3 bg-gray-700/50 border border-gray-600 rounded-lg text-white placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-blue-500/50 focus:border-blue-500 transition-all"
                  />
                </div>

                {/* Description Input */}
                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-2">Description</label>
                  <textarea
                    value={editContentForm.description}
                    onChange={(e) => setEditContentForm(prev => ({ ...prev, description: e.target.value }))}
                    placeholder="Enter a description"
                    rows={2}
                    className="w-full px-4 py-3 bg-gray-700/50 border border-gray-600 rounded-lg text-white placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-blue-500/50 focus:border-blue-500 transition-all resize-none"
                  />
                </div>

                {/* Category Selection */}
                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-2">Category</label>
                  <div className="flex gap-3">
                    <button
                      type="button"
                      onClick={() => setEditContentForm(prev => ({ ...prev, category: "free" }))}
                      className={`flex-1 py-3 rounded-lg font-medium transition-all ${
                        editContentForm.category === "free"
                          ? "bg-green-500/20 text-green-400 border-2 border-green-500"
                          : "bg-gray-700/50 text-gray-400 border border-gray-600 hover:border-gray-500"
                      }`}
                    >
                      Free
                    </button>
                    <button
                      type="button"
                      onClick={() => setEditContentForm(prev => ({ ...prev, category: "premium" }))}
                      className={`flex-1 py-3 rounded-lg font-medium transition-all ${
                        editContentForm.category === "premium"
                          ? "bg-amber-500/20 text-amber-400 border-2 border-amber-500"
                          : "bg-gray-700/50 text-gray-400 border border-gray-600 hover:border-gray-500"
                      }`}
                    >
                      Premium
                    </button>
                  </div>
                </div>

                {/* Action Buttons */}
                <div className="flex gap-3 pt-2">
                  <button
                    onClick={closeEditContentModal}
                    className="flex-1 py-3 border border-gray-600 text-gray-300 font-medium rounded-lg hover:bg-gray-700 transition-colors"
                  >
                    Cancel
                  </button>
                  <button
                    onClick={handleSaveContent}
                    disabled={savingContent}
                    className="flex-1 py-3 bg-gradient-to-r from-[#00A8E8] to-[#D000FF] text-white font-medium rounded-lg hover:opacity-90 transition-opacity disabled:opacity-50 flex items-center justify-center gap-2"
                  >
                    {savingContent ? (
                      <div className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                    ) : (
                      <>
                        <Save size={18} />
                        Save
                      </>
                    )}
                  </button>
                </div>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default AdminFanProfilePage;
